/**
 * （與 backend/src/shared/utils/zonedTime.ts 同步維護：兩邊的行為必須一致）
 *
 * 以 IANA 時區（例如 Asia/Taipei、America/New_York）處理「某地的牆上時間」與 UTC 瞬間的轉換。
 *
 * 為什麼不寫死 UTC+8：系統可能賣到國外，每個球場的時區由 CourseProfile.timezone 決定；
 * 伺服器容器的本地時區（通常是 UTC）不能拿來當依據。
 * 資料庫一律存 UTC 瞬間，只有在「解析使用者輸入的日期時間」與「判斷某瞬間落在哪一天」時才需要時區。
 */

const MILLISECONDS_PER_SECOND = 1000;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_PATTERN = /^(\d{2}):(\d{2})$/;
const DAYS_PER_WEEK = 7;

export interface ZonedDateParts {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
}

export function isValidTimeZone(timeZone: string): boolean {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone });
        return true;
    } catch {
        return false;
    }
}

/** 某個 UTC 瞬間，在指定時區的牆上時間各欄位 */
export function getZonedParts(instant: Date, timeZone: string): ZonedDateParts {
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });

    const values: Record<string, number> = {};
    for (const part of formatter.formatToParts(instant)) {
        if (part.type !== 'literal') {
            values[part.type] = Number(part.value);
        }
    }

    return {
        year: values.year,
        month: values.month,
        day: values.day,
        hour: values.hour,
        minute: values.minute,
        second: values.second,
    };
}

/** 指定時區在該瞬間與 UTC 的差（毫秒，東區為正） */
function getOffsetMilliseconds(instant: Date, timeZone: string): number {
    const parts = getZonedParts(instant, timeZone);
    const wallTimeAsUtc = Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour,
        parts.minute,
        parts.second
    );
    const instantWithoutMilliseconds =
        Math.floor(instant.getTime() / MILLISECONDS_PER_SECOND) * MILLISECONDS_PER_SECOND;
    return wallTimeAsUtc - instantWithoutMilliseconds;
}

/**
 * 把「該時區的牆上時間」轉成 UTC 瞬間。
 * 先用一次偏移估計，再用估計出的瞬間重新取偏移，跨夏令時間界線時才會正確。
 */
export function zonedWallTimeToUtc(
    year: number,
    month: number,
    day: number,
    hour: number,
    minute: number,
    timeZone: string
): Date {
    const wallTimeAsUtc = Date.UTC(year, month - 1, day, hour, minute);
    const firstGuess = new Date(
        wallTimeAsUtc - getOffsetMilliseconds(new Date(wallTimeAsUtc), timeZone)
    );
    return new Date(wallTimeAsUtc - getOffsetMilliseconds(firstGuess, timeZone));
}

function parseDateString(date: string): { year: number; month: number; day: number } {
    const match = DATE_PATTERN.exec(date);
    if (!match) {
        throw new Error(`日期格式必須是 YYYY-MM-DD：${date}`);
    }
    return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

/** 'YYYY-MM-DD' + 'HH:mm'（球場當地時間）-> UTC 瞬間 */
export function parseZonedDateTime(date: string, time: string, timeZone: string): Date {
    const { year, month, day } = parseDateString(date);
    const match = TIME_PATTERN.exec(time);
    if (!match) {
        throw new Error(`時間格式必須是 HH:mm：${time}`);
    }
    return zonedWallTimeToUtc(year, month, day, Number(match[1]), Number(match[2]), timeZone);
}

/** 球場當地某一天的 00:00:00.000（UTC 瞬間） */
export function startOfZonedDay(date: string, timeZone: string): Date {
    const { year, month, day } = parseDateString(date);
    return zonedWallTimeToUtc(year, month, day, 0, 0, timeZone);
}

/** 球場當地某一天的 23:59:59.999（UTC 瞬間）；夏令時間當天長度不是 24 小時也成立 */
export function endOfZonedDay(date: string, timeZone: string): Date {
    const { year, month, day } = parseDateString(date);
    const nextDayStart = zonedWallTimeToUtc(year, month, day + 1, 0, 0, timeZone);
    return new Date(nextDayStart.getTime() - 1);
}

/** 某個 UTC 瞬間，在球場當地是哪一天（YYYY-MM-DD） */
export function formatZonedDate(instant: Date, timeZone: string): string {
    const { year, month, day } = getZonedParts(instant, timeZone);
    return `${year}-${pad(month)}-${pad(day)}`;
}

/** 某個 UTC 瞬間，在球場當地的時間（HH:mm） */
export function formatZonedTime(instant: Date, timeZone: string): string {
    const { hour, minute } = getZonedParts(instant, timeZone);
    return `${pad(hour)}:${pad(minute)}`;
}

/** <input type="datetime-local"> 的值（YYYY-MM-DDTHH:mm），內容是球場當地的牆上時間 */
export function formatZonedDateTimeLocal(instant: Date, timeZone: string): string {
    return `${formatZonedDate(instant, timeZone)}T${formatZonedTime(instant, timeZone)}`;
}

/** formatZonedDateTimeLocal 的反向：datetime-local 的值（球場當地牆上時間）-> UTC 瞬間 */
export function parseZonedDateTimeLocal(value: string, timeZone: string): Date {
    const [date, time] = value.split('T');
    return parseZonedDateTime(date, time, timeZone);
}

/** 球場當地的星期幾（0 = 週日 … 6 = 週六，與 Date.getDay() 一致） */
export function getZonedDayOfWeek(instant: Date, timeZone: string): number {
    const { year, month, day } = getZonedParts(instant, timeZone);
    return new Date(Date.UTC(year, month - 1, day)).getUTCDay() % DAYS_PER_WEEK;
}

/** 球場當地「今天」的起訖（UTC 瞬間） */
export function getZonedDayRange(instant: Date, timeZone: string): { start: Date; end: Date } {
    const date = formatZonedDate(instant, timeZone);
    return { start: startOfZonedDay(date, timeZone), end: endOfZonedDay(date, timeZone) };
}

/** 'YYYY-MM-DD' 加減天數（純日曆運算，不涉及時區） */
export function addDaysToDateString(date: string, days: number): string {
    const { year, month, day } = parseDateString(date);
    const shifted = new Date(Date.UTC(year, month - 1, day) + days * MILLISECONDS_PER_DAY);
    return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
}

function pad(value: number): string {
    return String(value).padStart(2, '0');
}
